import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Loader2, Printer, Save, ArrowLeft, Wand2, History, Copy, RefreshCw } from "lucide-react";
import DuplicateInvoiceDialog from "@/components/invoices/DuplicateInvoiceDialog";
import { toast } from "sonner";
import { api, formatError, tablePath } from "@/lib/api";
import { blankInvoice, fromPrevious, prefillFromRecord, equipSummary, shortDate, today, PAGE2, hasPage2 } from "@/lib/invoice";
import NotesPanel from "@/components/invoices/NotesPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import DateField from "@/components/DateField";
import LineItems from "@/components/invoices/LineItems";
import InvoiceDocument, { FitWidth, InvoicePage2 } from "@/components/invoices/InvoiceDocument";

function Field({ label, children, className = "" }) {
  return <div className={`space-y-1.5 ${className}`}><Label>{label}</Label>{children}</div>;
}

function Section({ title, children }) {
  return <section className="border rounded-lg bg-card p-5 space-y-4"><p className="font-display text-lg font-bold">{title}</p>{children}</section>;
}

const CUSTOMER = [["name", "Facility / customer name"], ["phone", "Phone number"], ["address", "Address"], ["city_state_zip", "City, State ZIP"],
  ["administrator", "Administrator"], ["contact", "Contact"], ["corporate_info", "Corporate info"]];

export default function InvoiceEditor() {
  const { invoiceId } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [inv, setInv] = useState(null);
  const [dup, setDup] = useState(null);
  const [saving, setSaving] = useState(false);
  const [copying, setCopying] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const refreshEquipment = async () => {
    const src = inv.source || {};
    if (!src.db_id || !src.table || !src.record_id) return;
    setRefreshing(true);
    try {
      const { data } = await api.get(`${tablePath(src.db_id, src.table)}/records/${src.record_id}`);
      const fresh = prefillFromRecord(data.record, data.columns, src.db_id, src.table, settings);
      setInv((cur) => ({ ...cur, sticker_nos: fresh.sticker_nos, scales: fresh.scales, other_equipment: fresh.other_equipment, lifts: fresh.lifts }));
      toast.success("Equipment refreshed from the record — review and Save to keep it");
    } catch (e) { toast.error(formatError(e)); }
    setRefreshing(false);
  };

  useEffect(() => {
    (async () => {
      try {
        const s = (await api.get("/invoice-settings")).data;
        setSettings(s);
        setInv(invoiceId ? (await api.get(`/invoices/${invoiceId}`)).data : state?.prefill || blankInvoice(s));
      } catch (e) {
        toast.error(formatError(e));
      }
    })();
  }, [invoiceId, state]);

  if (!inv || !settings) return <Loader2 className="animate-spin text-primary" data-testid="invoice-loading" />;

  const set = (k, v) => setInv((s) => ({ ...s, [k]: v }));
  const setP2 = (k, v) => setInv((s) => ({ ...s, page2: { ...(s.page2 || {}), [k]: v } }));
  const setCust = (k) => (e) => setInv((s) => ({ ...s, customer: { ...s.customer, [k]: e.target.value } }));
  const toggleMonth = (i) => setInv((s) => ({ ...s, months: s.months.map((m, idx) => (idx === i ? !m : m)) }));

  const copyLast = async () => {
    setCopying(true);
    try {
      const { data } = await api.get("/invoices/last", { params: { customer: inv.customer.name } });
      if (data.id === invoiceId) throw new Error("This is already the latest invoice for this facility");
      setInv((cur) => ({ ...fromPrevious(data, cur), source: cur.source || data.source }));
      toast.success(`Copied from invoice ${data.number} (${shortDate(data.service_date)})`);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setCopying(false);
    }
  };

  const suggest = async () => {
    try {
      const { data } = await api.get("/invoices/next-number", { params: { service_date: inv.service_date } });
      set("number", data.number);
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const save = async (print) => {
    const win = print ? window.open("", "_blank") : null;
    setSaving(true);
    const body = { ...inv, subtotal: Number(inv.subtotal) || 0, tax_rate: Number(inv.tax_rate) || 0, parts: Number(inv.parts) || 0, plus_total: inv.plus_total === "" || inv.plus_total == null ? null : Number(inv.plus_total) };
    try {
      const { data } = invoiceId ? await api.put(`/invoices/${invoiceId}`, body) : await api.post("/invoices", body);
      setInv(data);
      toast.success(`Invoice ${data.number} saved`);
      if (win) win.location.href = `/invoices/${data.id}/print`;
      if (!invoiceId) navigate(`/invoices/${data.id}`, { replace: true });
    } catch (e) {
      win?.close();
      toast.error(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fade-up">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <button onClick={() => navigate("/invoices")} className="text-xs font-mono uppercase tracking-widest text-cyan-400 flex items-center gap-1 hover:underline" data-testid="back-to-invoices"><ArrowLeft size={12} /> Invoices</button>
          <h1 className="text-3xl font-bold tracking-tight mt-2" data-testid="invoice-editor-title">{invoiceId ? `Invoice ${inv.number}` : "New invoice"}</h1>
        </div>
        <div className="flex gap-2">
          {invoiceId && invoiceId !== "new" && <Button variant="outline" onClick={() => setDup({ ...inv, id: invoiceId })} data-testid="duplicate-invoice-button" className="hover:bg-secondary hover:text-foreground"><Copy size={16} /> Duplicate</Button>}
          <Button variant="outline" disabled={saving} onClick={() => save(true)} data-testid="save-print-invoice-button" className="hover:bg-secondary hover:text-foreground"><Printer size={16} /> Save & print</Button>
          <Button disabled={saving} onClick={() => save(false)} data-testid="save-invoice-button" className="hover:bg-blue-600 hover:text-white">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save
          </Button>
        </div>
      </div>

      <div className="mt-6 grid 2xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-8">
        <div className="space-y-6">
          <Section title="Invoice">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Invoice #">
                <div className="flex gap-1">
                  <Input value={inv.number} onChange={(e) => set("number", e.target.value)} placeholder="Auto on save" data-testid="invoice-number-input" />
                  <Button type="button" variant="outline" size="icon" onClick={suggest} title="Suggest next number" data-testid="suggest-number-button"><Wand2 size={14} /></Button>
                </div>
              </Field>
              <Field label="Service date"><DateField id="invoice-service-date" value={inv.service_date} onChange={(v) => set("service_date", v || inv.service_date)} /></Field>
              <Field label="Tech"><Input value={inv.tech} onChange={(e) => set("tech", e.target.value)} data-testid="invoice-tech" /></Field>
              <Field label="Status">
                <Select value={inv.status} onValueChange={(v) => setInv((s) => ({ ...s, status: v, date_paid: v === "paid" ? s.date_paid || today() : s.date_paid }))}>
                  <SelectTrigger data-testid="invoice-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card">
                    {["unpaid", "paid", "draft"].map((s) => <SelectItem key={s} value={s} className="capitalize" data-testid={`status-${s}`}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Date Pd"><DateField id="invoice-date-paid" value={inv.date_paid} onChange={(v) => set("date_paid", v || null)} /></Field>
              <Field label="Payment note"><Input value={inv.payment_note || ""} onChange={(e) => set("payment_note", e.target.value)} maxLength={300} placeholder="e.g. Check 1043" data-testid="invoice-payment-note" /></Field>
            </div>
          </Section>
          <Section title="Customer">
            <div className="flex flex-wrap items-center justify-between gap-2 -mt-2">
              <p className="text-xs text-muted-foreground">Fill the equipment, schedule and price from this facility's most recent invoice.</p>
              <Button type="button" size="sm" variant="outline" onClick={copyLast} disabled={copying || !inv.customer.name.trim()} data-testid="copy-last-visit-button">
                {copying ? <Loader2 size={14} className="animate-spin" /> : <History size={14} />} Copy last visit
              </Button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {CUSTOMER.map(([k, label]) => (
                <Field key={k} label={label}><Input value={inv.customer[k]} onChange={setCust(k)} data-testid={`customer-${k}`} /></Field>
              ))}
            </div>
          </Section>
          <Section title="Service schedule">
            <div className="flex flex-wrap gap-x-4 gap-y-2" data-testid="month-checkboxes">
              {inv.months.map((m, i) => (
                <label key={i} className="flex items-center gap-1.5 text-sm cursor-pointer">
                  <Checkbox checked={m} onCheckedChange={() => toggleMonth(i)} data-testid={`month-${i + 1}`} /> {i + 1}
                </label>
              ))}
            </div>
            <Field label="Frequency" className="max-w-[160px]"><Input value={inv.frequency} onChange={(e) => set("frequency", e.target.value)} data-testid="invoice-frequency" /></Field>
          </Section>
          <Section title="Service performed">
            <Textarea rows={4} value={inv.service_performed} onChange={(e) => set("service_performed", e.target.value)} data-testid="invoice-service-performed-input" />
          </Section>
          <Section title="Equipment">
            <div className="flex items-center justify-between gap-3 flex-wrap -mt-1">
              <p className="text-sm text-muted-foreground" data-testid="equip-count">{equipSummary(inv) || "No equipment listed"}</p>
              {inv.source?.record_id && inv.source?.db_id && inv.source?.table && (
                <Button type="button" variant="outline" size="sm" onClick={refreshEquipment} disabled={refreshing} data-testid="refresh-equipment-button" className="hover:bg-secondary hover:text-foreground">
                  {refreshing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Refresh equipment from record
                </Button>
              )}
            </div>
            <Field label="Sticker Nos"><Input value={inv.sticker_nos} onChange={(e) => set("sticker_nos", e.target.value)} data-testid="invoice-sticker-nos" /></Field>
            <Field label="Scales"><LineItems items={inv.scales} onChange={(v) => set("scales", v)} testId="scale" placeholder="M# 758C  S# E25208-0262  Detecto 800 lb digital wheelchair scale." /></Field>
            <Field label="Other equipment"><LineItems items={inv.other_equipment} onChange={(v) => set("other_equipment", v)} testId="other" placeholder="Model, serial and description" /></Field>
            <Field label="Lifts"><LineItems items={inv.lifts} onChange={(v) => set("lifts", v)} testId="lift" placeholder="M# RPL600-1  S# 21HM015622  Invacare 600 lb electric patient lift." /></Field>
          </Section>
          <Section title="Page 2 – notes & tax codes">
            <Field label="Special Notes"><Textarea rows={4} value={inv.page2?.["Special Notes"] || ""} onChange={(e) => setP2("Special Notes", e.target.value)} data-testid="page2-Special-Notes" /></Field>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {PAGE2.slice(1).map((k) => (
                <Field key={k} label={k}><Input value={inv.page2?.[k] || ""} onChange={(e) => setP2(k, e.target.value)} data-testid={`page2-${k.replace(/\s+/g, "-")}`} /></Field>
              ))}
            </div>
          </Section>
          <Section title="Amounts">
            <div className="grid sm:grid-cols-4 gap-4">
              <Field label="Subtotal ($)"><Input type="number" step="any" min="0" value={inv.subtotal} onChange={(e) => set("subtotal", e.target.value)} data-testid="invoice-subtotal" /></Field>
              <Field label="Tax rate (%)"><Input type="number" step="any" min="0" value={inv.tax_rate} onChange={(e) => set("tax_rate", e.target.value)} data-testid="invoice-tax-rate" /></Field>
              <Field label="Parts ($, no tax)"><Input type="number" step="any" min="0" placeholder="Leave blank for none" value={inv.parts || ""} onChange={(e) => set("parts", e.target.value)} data-testid="invoice-parts" /></Field>
              <Field label="Terms"><Input value={inv.terms} onChange={(e) => set("terms", e.target.value)} data-testid="invoice-terms" /></Field>
              <Field label="+Total ($)"><Input type="number" step="any" min="0" placeholder="Enter amount (blank = none)" value={inv.plus_total ?? ""} onChange={(e) => set("plus_total", e.target.value)} data-testid="invoice-plus-total" /></Field>
            </div>
          </Section>
        </div>
        <div className="min-w-0">
          <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Live preview</p>
          <div className="2xl:sticky 2xl:top-4"><NotesPanel source={inv.source} /><FitWidth><InvoiceDocument invoice={inv} settings={settings} /></FitWidth>
            {hasPage2(inv) && <div className="mt-6"><FitWidth><InvoicePage2 invoice={inv} settings={settings} /></FitWidth></div>}</div>
        </div>
      </div>
      <DuplicateInvoiceDialog inv={dup} onClose={() => setDup(null)} />
    </div>
  );
}
